import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    IoPeopleOutline,
    IoAddOutline,
    IoSearchOutline,
    IoTrashOutline,
    IoPencilOutline,
    IoDocumentTextOutline,
    IoArrowBack,
    IoShieldCheckmarkOutline,
    IoCalendarOutline
} from 'react-icons/io5';
import moment from 'moment';
import './GestionEmpleadosScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';

function GestionEmpleadosScreen() {
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const { currentUser, userData } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determinamos el contexto SaaS
    const isSaaS = location.pathname.includes('/superuser/hr');
    const basePath = isSaaS ? '/superuser/hr' : '';
    const activeCondominioId = isSaaS ? 'SAAS' : userData?.condominioId;

    // Helper de Permisos
    const checkPermiso = (permiso) => {
        if (userData?.rol === 'superuser' || userData?.rol === 'masteruser') {
            return !!userData?.permisos?.[permiso];
        }
        return true; 
    };

    useEffect(() => {
        const fetchEmpleados = async () => {
            if (!currentUser) return;
            setLoading(true);
            try {
                // If it's a superUser, they might see all or filter by condo. For now, we fetch all.
                // Assuming "condominioId" if admin, or global if superadmin.
                // Assuming "condominioId" if admin, or global if superadmin.
                let q = collection(db, "empleados");
                const snap = await getDocs(q);
                const empData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // Filter by the active context (SaaS or regular Condominio)
                // If it's a regular superUser looking at a Condominio, they shouldn't be here (use specific admin views or global views), 
                // but for now we enforce the activeCondominioId strictly.
                const filtered = empData.filter(e => e.condominioId === activeCondominioId);

                setEmpleados(filtered);
            } catch (error) {
                console.error("Error fetching empleados:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchEmpleados();
    }, [currentUser, userData]);

    const handleDelete = async (id, nombre) => {
        if (!window.confirm(`¿Estás seguro de dar de baja al empleado ${nombre}? Esto no borrará su historial de nóminas.`)) return;
        try {
            await updateDoc(doc(db, "empleados", id), { estatus: 'Inactivo', fechaBaja: new Date() });
            setEmpleados(empleados.map(e => e.id === id ? { ...e, estatus: 'Inactivo' } : e));
            alert("Empleado dado de baja exitosamente.");
        } catch (error) {
            console.error("Error deleting empleado:", error);
            alert("Error al dar de baja.");
        }
    };

    const filteredEmpleados = empleados.filter(emp =>
        (emp.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.puesto || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="empleados-loading">Cargando expediente...</div>;

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute={isSaaS ? "/superuser/hr/gestion-empleados" : "/nominas-dashboard"} />
            <main className="ks-main">
                <SafeAreaInsets top={false} bottom={true} />
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>
                <div className="ks-scroll-area">
                    <div className="gestion-empleados-container" style={{ padding: '20px', paddingTop: '0px', maxWidth: '100%', minHeight: 'auto', background: 'transparent' }}>
                        {/* OMNI-BANNER PREMIUM */}
                        <div className="usuarios-fixed-wrapper" style={{ position: 'relative', margin: '0 0 20px 0', padding: 0, borderBottom: 'none', background: 'transparent' }}>
                            <div className="usuarios-banner-compact" style={{ background: isSaaS ? 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)' : 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)' }}>
                                <button onClick={() => navigate(isSaaS ? '/superuser/hr/dashboard' : '/nominas-dashboard')} className="usuarios-back-btn glass">
                                    <IoArrowBack size={20} />
                                </button>

                                <div className="usuarios-banner-content">
                                    <h2>{isSaaS ? 'Corporativo: Plantilla' : 'Recursos Humanos'}</h2>
                                    <p>{isSaaS ? 'Gestión interna de empleados de la empresa' : 'Gestión de Empleados, Salarios y Prestaciones'}</p>
                                </div>

                                <div className="usuarios-actions">
                                    <div className="usuarios-search-box">
                                        <IoSearchOutline className="search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Buscar empleado..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    {checkPermiso('crear_empleados') && (
                                        <button className="action-btn-primary glass-btn" onClick={() => navigate(`${basePath}/nuevo-empleado`)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <IoAddOutline size={18} /> Nuevo Empleado
                                        </button>
                                    )}
                                </div>

                                <IoPeopleOutline className="usuarios-banner-icon" />
                            </div>
                        </div>

                        <div className="empleados-content-scroll" style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}>
                            {/* Desktop View */}
                            <div className="desktop-table-wrapper table-responsive glass-card" style={{ background: 'white' }}>
                                <table className="ag-table">
                                    <thead>
                                        <tr>
                                            <th>Empleado</th>
                                            <th>Puesto</th>
                                            <th>Salario Bruto</th>
                                            <th>Periodo</th>
                                            <th>Estatus</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEmpleados.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                                                    No hay empleados registrados.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredEmpleados.map(emp => (
                                                <tr key={emp.id} className={emp.estatus === 'Inactivo' ? 'row-inactive' : ''}>
                                                    <td>
                                                        <div className="emp-name">{emp.nombre} {emp.apellidos}</div>
                                                        <div className="emp-rfc">{emp.rfc || 'Sin RFC'}</div>
                                                    </td>
                                                    <td>{emp.puesto}</td>
                                                    <td className="emp-salary">
                                                        {emp.salarioBruto ? `$${Number(emp.salarioBruto).toLocaleString('es-MX', { minimumFractionDigits: 2 })} ` : 'N/A'}
                                                    </td>
                                                    <td>{emp.periodoPago || 'Quincenal'}</td>
                                                    <td>
                                                        <span className={`status-badge ${emp.estatus === 'Inactivo' ? 'bg-danger' : 'bg-success'} `}>
                                                            {emp.estatus || 'Activo'}
                                                        </span>
                                                    </td>
                                                    <td className="actions-cell">
                                                        {checkPermiso('editar_empleados') && (
                                                            <button
                                                                className="action-btn edit"
                                                                title="Editar Expediente"
                                                                onClick={() => navigate(`${basePath}/editar-empleado/${emp.id}`)}
                                                            >
                                                                <IoPencilOutline size={18} />
                                                            </button>
                                                        )}
                                                        <button
                                                            className="action-btn view"
                                                            title="Prestaciones, Aguinaldo y Salario"
                                                            onClick={() => navigate(`${basePath}/prestaciones-empleado/${emp.id}`)}
                                                            style={{ backgroundColor: '#fef3c7', color: '#d97706' }}
                                                        >
                                                            <IoShieldCheckmarkOutline size={18} />
                                                        </button>
                                                        <button
                                                            className="action-btn view"
                                                            title="Historial de Incidencias e Inasistencias"
                                                            onClick={() => navigate(`${basePath}/incidencias-empleado/${emp.id}`)}
                                                            style={{ backgroundColor: '#f5f3ff', color: '#8b5cf6' }}
                                                        >
                                                            <IoCalendarOutline size={18} />
                                                        </button>
                                                        {emp.estatus !== 'Inactivo' && checkPermiso('editar_empleados') && (
                                                            <button
                                                                className="action-btn delete"
                                                                title="Dar de Baja"
                                                                onClick={() => handleDelete(emp.id, emp.nombre)}
                                                            >
                                                                <IoTrashOutline size={18} />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile View */}
                            <div className="saas-mobile-cards">
                                {filteredEmpleados.length === 0 ? (
                                    <div className="glass-card" style={{ padding: '20px', textAlign: 'center', color: '#64748b', background: 'white' }}>
                                        No hay empleados registrados.
                                    </div>
                                ) : (
                                    filteredEmpleados.map(emp => (
                                        <div key={emp.id} className={`superuser-mobile-card ${emp.estatus === 'Inactivo' ? 'row-inactive' : ''}`}>
                                            <div className="card-top-row">
                                                <div className="emp-name" style={{ fontSize: '1rem' }}>
                                                    {emp.nombre} {emp.apellidos}
                                                </div>
                                                <span className={`status-badge ${emp.estatus === 'Inactivo' ? 'bg-danger' : 'bg-success'}`}>
                                                    {emp.estatus || 'Activo'}
                                                </span>
                                            </div>
                                            
                                            <div className="card-details-grid">
                                                <div className="detail-item">
                                                    <span className="detail-label">RFC:</span>
                                                    <span className="detail-val">{emp.rfc || 'Sin RFC'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Puesto:</span>
                                                    <span className="detail-val">{emp.puesto || 'Sin Puesto'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Salario Bruto:</span>
                                                    <span className="detail-val emp-salary">
                                                        {emp.salarioBruto ? `$${Number(emp.salarioBruto).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : 'N/A'}
                                                    </span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Periodo:</span>
                                                    <span className="detail-val">{emp.periodoPago || 'Quincenal'}</span>
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px', borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
                                                {checkPermiso('editar_empleados') && (
                                                    <button
                                                        className="action-btn edit"
                                                        title="Editar Expediente"
                                                        onClick={() => navigate(`${basePath}/editar-empleado/${emp.id}`)}
                                                    >
                                                        <IoPencilOutline size={18} />
                                                    </button>
                                                )}
                                                <button
                                                    className="action-btn view"
                                                    title="Prestaciones, Aguinaldo y Salario"
                                                    onClick={() => navigate(`${basePath}/prestaciones-empleado/${emp.id}`)}
                                                    style={{ backgroundColor: '#fef3c7', color: '#d97706' }}
                                                >
                                                    <IoShieldCheckmarkOutline size={18} />
                                                </button>
                                                <button
                                                    className="action-btn view"
                                                    title="Historial de Incidencias e Inasistencias"
                                                    onClick={() => navigate(`${basePath}/incidencias-empleado/${emp.id}`)}
                                                    style={{ backgroundColor: '#f5f3ff', color: '#8b5cf6' }}
                                                >
                                                    <IoCalendarOutline size={18} />
                                                </button>
                                                {emp.estatus !== 'Inactivo' && checkPermiso('editar_empleados') && (
                                                    <button
                                                        className="action-btn delete"
                                                        title="Dar de Baja"
                                                        onClick={() => handleDelete(emp.id, emp.nombre)}
                                                    >
                                                        <IoTrashOutline size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <KondoBottomNav activeRoute={isSaaS ? "/superuser/hr/gestion-empleados" : "/nominas-dashboard"} />
            </main>
        </div>
    );
}

export default GestionEmpleadosScreen;
